/* Excluding one component from a viewpoint, as a traversal filter.

   A traversal filter is attached to an endpoint of a triple, not to the dataset, and its rules are
   advanced-search rules. So "hide this component" becomes a rule that every component reaching
   that endpoint must satisfy: `_id != <the component>`.

   Type does not come into it: a negation by id can only ever exclude the component it names, so
   the rule needs no knowledge of what type sits where, and the same component reached at several
   positions is covered at all of them.

   Which endpoints get the rule follows `pathsToGraph`, which is what decides the node a filter
   belongs to. Each triple introduces one node — its target when outgoing, its source when
   incoming — and the first triple's other end is the start node. Together those are every
   distinct component position in the traversal, each with exactly one filter. Writing to both
   ends of every triple instead would mint a second filter for nodes that already have one, which
   the traversal tolerates but the viewpoint builder does not: it maps one filter per node and
   logs an error for the rest. */

import {
  BooleanOperator,
  type ArdoqId,
  type LocalFilterId,
  type Path,
  type TraversalParams,
  type ViewpointBuilderFilterPerNode,
  type ViewpointBuilderFilters,
  Operator,
  ReferenceDirection,
} from '@ardoq/api-types';
import { isTruthy } from '@ardoq/common-helpers';
import { v4 as uuidv4 } from 'uuid';
import { pathToKey } from 'viewpointBuilder/traversals/pathToKey';

type ExclusionRule = ViewpointBuilderFilterPerNode['rules'][number];

/**
 * Exported for the viewpoint builder, which drops rules it has no filter definition for. `_id` is
 * not a user field, so an exclusion is one of those — and dropping it on load means deleting it on
 * save, since the builder rebuilds the traversal from what it loaded.
 */
export const isExclusionRule = (rule: ExclusionRule): boolean =>
  rule.field === '_id' && rule.operator === Operator.NOT_EQUAL;

/**
 * How many rules in a traversal's filters a reader should report as the user's.
 *
 * Exclusions share the `filters` map with real filters, so every plain `rules.length` sum counted
 * them - and one exclusion mints one rule per graph node, so a three-hop traversal with no filters
 * at all reported three. Worse than the number being wrong: both readers offer an Edit button onto
 * the viewpoint builder's Filters tab, which counts only rules it has a definition for, so the user
 * was told about filters and then shown an empty panel.
 *
 * The count that does stand for exclusions is the one on the toolbar, and it is per component
 * rather than per rule.
 */
export const countAppliedFilterRules = (
  filters: ViewpointBuilderFilters | undefined
): number =>
  Object.values(filters ?? {}).reduce(
    (total, { rules }) =>
      total + rules.filter(rule => !isExclusionRule(rule)).length,
    0
  );

const toExclusionRule = (componentId: ArdoqId): ExclusionRule => ({
  id: '_id',
  field: '_id',
  input: 'text',
  type: 'string',
  operator: Operator.NOT_EQUAL,
  value: componentId,
});

/**
 * A filter this cannot safely add a rule to.
 *
 * Rules within a filter share one `condition` and cannot nest, so an exclusion appended to an
 * `OR` filter widens it instead of narrowing it — `(name is A OR name is B OR _id is not X)`
 * matches everything. One rule under `OR` means the same as under `AND`, so only a filter that
 * is already combining several is out of reach.
 */
const rejectsMoreRules = (filter: ViewpointBuilderFilterPerNode): boolean =>
  filter.condition !== BooleanOperator.AND && filter.rules.length > 1;

const isOutgoing = (triple: Path[number]) =>
  triple.direction === ReferenceDirection.OUTGOING;

/** The endpoint whose node this triple introduces. See the note at the top of the file. */
const nodeEndpoint = (triple: Path[number]) =>
  isOutgoing(triple) ? triple.targetFilter : triple.sourceFilter;

/** The first triple's other end, which is the start node every path shares. */
const startEndpoint = (triple: Path[number]) =>
  isOutgoing(triple) ? triple.sourceFilter : triple.targetFilter;

/** Every endpoint holding a component filter, wherever it came from. */
const componentFilterIdsIn = (paths: Path[]): (LocalFilterId | undefined)[] =>
  paths.flatMap(path =>
    path.flatMap(({ sourceFilter, targetFilter }) => [
      sourceFilter,
      targetFilter,
    ])
  );

/** Only the endpoints an exclusion would write to. */
const targetedFilterIdsIn = (paths: Path[]): LocalFilterId[] =>
  paths
    .flatMap(path => [
      path.length > 0 ? startEndpoint(path[0]) : undefined,
      ...path.map(nodeEndpoint),
    ])
    .filter(isTruthy);

/**
 * Why a traversal cannot take an exclusion.
 *
 * A union rather than a boolean because the two causes need different things said about them, and
 * the first version of this was a conjunction reported with one message - so a start-set-only
 * dataset was told to remove an OR filter it did not have, from a panel where nothing would help.
 * Naming the cause makes the message a `switch` the compiler checks.
 */
export type ExclusionRefusal =
  /* `toTraversalLoadedState` produces this for a dataset that is only a start set. There is no
     endpoint to hang a rule on, so an exclusion would write a filter no triple references:
     nothing hidden, and nothing able to find it again. */
  | 'no-paths'
  /* An OR condition on a targeted filter. Adding an exclusion to it widens the result instead of
     narrowing it - the rule the user cannot see would be satisfied by everything the others reject. */
  | 'or-condition';

export const exclusionRefusal = ({
  paths,
  filters,
}: TraversalParams): ExclusionRefusal | null => {
  if (paths.length === 0) return 'no-paths';

  const rejectsMore = targetedFilterIdsIn(paths)
    .map(filterId => filters[filterId])
    .some(filter => filter && rejectsMoreRules(filter));

  return rejectsMore ? 'or-condition' : null;
};

export const canExcludeComponent = (params: TraversalParams): boolean =>
  exclusionRefusal(params) === null;

/** The components this traversal is currently excluding, in no particular order. */
export const getExcludedComponentIds = ({
  paths,
  filters,
}: TraversalParams): ArdoqId[] => {
  const ids = new Set<ArdoqId>();
  componentFilterIdsIn(paths).forEach(filterId => {
    if (!filterId) return;
    filters[filterId]?.rules.filter(isExclusionRule).forEach(({ value }) => {
      if (typeof value === 'string') ids.add(value);
    });
  });
  return [...ids];
};

const withRule = (
  filter: ViewpointBuilderFilterPerNode,
  rule: ExclusionRule
): ViewpointBuilderFilterPerNode => ({
  ...filter,
  // Safe by `rejectsMoreRules`: a filter of one rule means the same either way.
  condition: BooleanOperator.AND,
  rules: [...filter.rules, rule],
});

const newFilter = (
  filterId: LocalFilterId,
  rule: ExclusionRule
): ViewpointBuilderFilterPerNode => ({
  filterId,
  condition: BooleanOperator.AND,
  rules: [rule],
});

/**
 * Adds the exclusion to one endpoint per node, minting a filter where a node has none.
 *
 * Returns the paths alongside the filters because a node without a filter gains one, and its
 * triple has to point at it.
 *
 * Minted ids are keyed by node identity, not by the id they produce. A node with no filter yet
 * arrives as `undefined` at every path running through it, so keying on the result would mint a
 * fresh id per occurrence — and branching traversals share prefixes as a matter of course. Two
 * ids for one node is the case the note at the top of this file exists to avoid: `pathsToGraph`
 * registers only the first, and the viewpoint builder drops and then deletes the rest. Identity
 * is the type-path prefix, hashed the same way `pathsToGraph` hashes it.
 */
const addExclusionToPaths = (
  { paths, filters }: TraversalParams,
  componentId: ArdoqId
): { paths: Path[]; filters: ViewpointBuilderFilters } => {
  const nextFilters: ViewpointBuilderFilters = { ...filters };
  const rule = toExclusionRule(componentId);
  const written = new Set<LocalFilterId>();
  const mintedByNode = new Map<string, LocalFilterId>();

  /** `nodeKey` is null for an endpoint whose node this call does not identify. */
  const attach = (
    filterId: LocalFilterId | undefined,
    nodeKey: string | null
  ): LocalFilterId => {
    const minted = nodeKey === null ? undefined : mintedByNode.get(nodeKey);
    const id = filterId ?? minted ?? uuidv4();
    if (nodeKey !== null && !filterId) mintedByNode.set(nodeKey, id);
    if (written.has(id)) return id;
    written.add(id);
    nextFilters[id] = nextFilters[id]
      ? withRule(nextFilters[id], rule)
      : newFilter(id, rule);
    return id;
  };

  /* The start node is one node every path shares, and `pathsToGraph` identifies it by the empty
     prefix rather than by a triple. */
  const startFilterId = attach(
    paths
      .map(path => (path.length > 0 ? startEndpoint(path[0]) : undefined))
      .find(isTruthy),
    pathToKey([])
  );

  const nextPaths = paths.map(path =>
    path.map((triple, index) => {
      const nodeKey = pathToKey(path.slice(0, index + 1));
      const isStart = index === 0;
      return isOutgoing(triple)
        ? {
            ...triple,
            ...(isStart ? { sourceFilter: startFilterId } : {}),
            targetFilter: attach(triple.targetFilter, nodeKey),
          }
        : {
            ...triple,
            ...(isStart ? { targetFilter: startFilterId } : {}),
            sourceFilter: attach(triple.sourceFilter, nodeKey),
          };
    })
  );

  return { paths: nextPaths, filters: nextFilters };
};

/**
 * The traversal with one more component excluded, or the traversal unchanged when it already
 * excludes it. Callers gate on {@link canExcludeComponent} first.
 */
export const excludeComponent = (
  params: TraversalParams,
  componentId: ArdoqId
): TraversalParams => {
  /* Guarded here as well as in `canExcludeComponent`, so the operation cannot mint a filter no
     triple references however it is called. */
  if (params.paths.length === 0) return params;
  if (getExcludedComponentIds(params).includes(componentId)) return params;
  return { ...params, ...addExclusionToPaths(params, componentId) };
};

/**
 * The traversal with every exclusion lifted.
 *
 * Filters left with no rules are dropped and their endpoints unset, so undoing returns the
 * traversal to the shape it had rather than leaving empty filters behind for the Dataset
 * settings panel to render.
 */
const withoutExclusions = (
  params: TraversalParams,
  /** The component to stop excluding, or null for every one of them. */
  componentId: ArdoqId | null
): TraversalParams => {
  const shouldDrop = (rule: ExclusionRule) =>
    isExclusionRule(rule) &&
    (componentId === null || rule.value === componentId);

  const nextFilters: ViewpointBuilderFilters = {};
  Object.entries(params.filters).forEach(([filterId, filter]) => {
    const rules = filter.rules.filter(rule => !shouldDrop(rule));
    if (rules.length > 0) nextFilters[filterId] = { ...filter, rules };
  });

  const keep = (filterId: LocalFilterId | undefined) =>
    filterId && nextFilters[filterId] ? filterId : undefined;

  return {
    ...params,
    filters: nextFilters,
    paths: params.paths.map(path =>
      path.map(triple => ({
        ...triple,
        sourceFilter: keep(triple.sourceFilter),
        targetFilter: keep(triple.targetFilter),
        /* Reference filters share the id space with node filters, and the prune above drops any
           filter left with no rules — including one that arrived empty, which saved data can
           carry when every rule of a filter row fails validation. Left untouched, the triple
           would point at an id `filters` no longer holds. */
        referenceFilter: keep(triple.referenceFilter),
      }))
    ),
  };
};

/**
 * The traversal no longer excluding one component, with any other exclusions left alone.
 *
 * What the confirmation toast's Undo reverts. Clearing everything there would discard exclusions
 * the user made earlier in the session, and any the viewpoint was saved with.
 */
export const clearExcludedComponent = (
  params: TraversalParams,
  componentId: ArdoqId
): TraversalParams => withoutExclusions(params, componentId);

/** The traversal with every exclusion lifted. What the toolbar's bulk control does. */
export const clearExcludedComponents = (
  params: TraversalParams
): TraversalParams => withoutExclusions(params, null);
